import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, AlertCircle, ToggleLeft, ToggleRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROOM_ACTION_CONFIG = {
  pop_your_up: {
    title: 'POP YOUR OPP',
    price: '$45',
    priceNum: 45,
    variationId: 'H7VCLYKOBFEHJH3UPKOJAVU6',
    route: '/pop-your-opp',
    color: '#EF4444',
    targetLabel: 'Name of the person being targeted',
    targetPlaceholder: 'Enter their name',
    hasTargetName: true,
    hasTargetGroup: false,
    subtitle: null,
  },
  gu_them: {
    title: 'GU THEM',
    price: '$15',
    priceNum: 15,
    variationId: 'UST3GEG3GVI66KOMGNAATUZA',
    route: '/gu-them',
    color: '#F59E0B',
    targetLabel: 'Name of the person being targeted',
    targetPlaceholder: 'Enter their name',
    hasTargetName: true,
    hasTargetGroup: false,
    subtitle: null,
  },
  one_mic_individual: {
    title: 'ONE MIC INDIVIDUAL',
    price: '$7',
    priceNum: 7,
    variationId: 'E7LUGQ3W5NOYJXXK6ML2XN2Y',
    route: '/one-mic-individual',
    color: '#3B82F6',
    targetLabel: 'Name of the individual receiving One Mic',
    targetPlaceholder: 'Enter their name',
    hasTargetName: true,
    hasTargetGroup: false,
    subtitle: '5 Minutes',
  },
  one_mic_group: {
    title: 'ONE MIC MAN OR WOMAN',
    price: '$10',
    priceNum: 10,
    variationId: 'B7HBMBOXQ5EC6JGJXUHVXBRV',
    route: '/one-mic-man-or-woman',
    color: '#8B5CF6',
    targetLabel: 'Select MAN or WOMAN',
    hasTargetName: false,
    hasTargetGroup: true,
    subtitle: '10 Minutes',
  },
  come_back_goo: {
    title: 'COME BACK FROM THE GU',
    price: '$15',
    priceNum: 15,
    variationId: 'AF755U3I7HQTPT43O7KKLFPY',
    route: '/come-back-from-the-gu',
    color: '#10B981',
    targetLabel: 'Who is coming back from the gu?',
    targetPlaceholder: 'Enter their name',
    hasTargetName: true,
    hasTargetGroup: false,
    subtitle: null,
  },
  come_back_popped: {
    title: 'COME BACK FROM BEING POPPED',
    price: '$45',
    priceNum: 45,
    variationId: 'RDG36QJJ745Z4NPNMD7ZQSFM',
    route: '/come-back-from-being-popped',
    color: '#EC4899',
    targetLabel: 'Who is being brought back?',
    targetPlaceholder: 'Enter their name',
    hasTargetName: true,
    hasTargetGroup: false,
    subtitle: null,
  },
};

const RoomActionPage = ({ actionType }) => {
  const config = ROOM_ACTION_CONFIG[actionType];
  const [step, setStep] = useState('form');
  const [targetName, setTargetName] = useState('');
  const [targetGroup, setTargetGroup] = useState('');
  const [purchaserName, setPurchaserName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'complete') {
      window.history.replaceState({}, '', window.location.pathname);
      const pendingId = sessionStorage.getItem(`med_pending_ra_${actionType}_id`);
      const orderId = sessionStorage.getItem(`med_pending_ra_${actionType}_order`);
      if (pendingId && orderId) {
        setStep('confirming');
        startPolling(pendingId, orderId);
      }
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const startPolling = (pendingId, orderId) => {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API}/api/room-actions/pending/${pendingId}/status?order_id=${orderId}`);
        const data = await res.json();
        if (data.status && data.status !== 'pending_payment') {
          clearInterval(pollRef.current);
          sessionStorage.removeItem(`med_pending_ra_${actionType}_id`);
          sessionStorage.removeItem(`med_pending_ra_${actionType}_order`);
          if (data.status === 'expired') {
            setError('Request expired. Please try again.');
            setStep('error');
          } else {
            setStep('submitted');
          }
          return;
        }
      } catch (e) { /* retry */ }
      if (attempts >= 60) {
        clearInterval(pollRef.current);
        setError('Payment verification timed out. If you were charged, your action will be processed automatically.');
        setStep('error');
      }
    }, 2000);
  };

  const isFormValid = () => {
    if (config.hasTargetName && !targetName.trim()) return false;
    if (config.hasTargetGroup && !targetGroup) return false;
    return true;
  };

  const handlePayAndSubmit = async () => {
    if (!isFormValid()) {
      setError(config.hasTargetGroup ? 'Please select MAN or WOMAN.' : 'Target name is required.');
      return;
    }
    setError('');
    setStep('processing');
    try {
      const pendingRes = await fetch(`${API}/api/room-actions/pending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action_type: actionType,
          target_name: config.hasTargetName ? targetName.trim() : null,
          target_group: config.hasTargetGroup ? targetGroup : null,
          purchaser_name: purchaserName.trim() || null,
          is_anonymous: isAnonymous,
          message: message.trim() || null,
        }),
      });
      if (!pendingRes.ok) {
        const err = await pendingRes.json();
        throw new Error(err.detail || 'Failed to save action');
      }
      const { pending_id } = await pendingRes.json();

      const checkoutRes = await fetch(`${API}/api/store/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variation_id: config.variationId,
          quantity: 1,
          entitlement_type: 'room_action',
          redirect_path: config.route,
          pending_submission_id: pending_id,
        }),
      });
      if (!checkoutRes.ok) throw new Error('Failed to create checkout');
      const { url, order_id } = await checkoutRes.json();

      sessionStorage.setItem(`med_pending_ra_${actionType}_id`, pending_id);
      sessionStorage.setItem(`med_pending_ra_${actionType}_order`, order_id);
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setStep('form');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0a2e] to-[#0d1b2a] flex items-center justify-center p-4" data-testid={`room-action-page-${actionType}`}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-6">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-full border mb-4"
            style={{ backgroundColor: `${config.color}20`, borderColor: `${config.color}50` }}
          >
            <span className="text-2xl font-black" style={{ color: config.color }}>
              {actionType === 'pop_your_up' ? 'P' : actionType === 'gu_them' ? 'G' : actionType === 'come_back_goo' ? 'C' : actionType === 'come_back_popped' ? 'B' : 'M'}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-white" data-testid="room-action-title">{config.title}</h1>
          <p className="text-white/60 text-sm mt-1">{config.price} per action</p>
          {config.subtitle && <p className="text-white/40 text-xs mt-0.5">{config.subtitle}</p>}
        </div>

        {step === 'form' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-6 space-y-4" data-testid="room-action-form">
            {/* Target Name or Target Group */}
            {config.hasTargetName && (
              <div>
                <label className="block text-sm mb-1" style={{ color: `${config.color}CC` }}>{config.targetLabel} *</label>
                <input
                  type="text" value={targetName} onChange={e => setTargetName(e.target.value)}
                  placeholder={config.targetPlaceholder} maxLength={200}
                  className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none"
                  style={{ borderColor: targetName.trim() ? `${config.color}50` : undefined }}
                  data-testid="input-target-name"
                />
              </div>
            )}
            {config.hasTargetGroup && (
              <div>
                <label className="block text-sm mb-2" style={{ color: `${config.color}CC` }}>{config.targetLabel} *</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setTargetGroup('MAN')}
                    className={`flex-1 py-3 rounded-lg font-bold text-sm border transition-all ${
                      targetGroup === 'MAN'
                        ? 'border-blue-400 bg-blue-400/20 text-blue-300'
                        : 'border-white/20 bg-black/20 text-white/40 hover:border-white/30'
                    }`}
                    data-testid="select-man"
                  >
                    MAN
                  </button>
                  <button
                    type="button"
                    onClick={() => setTargetGroup('WOMAN')}
                    className={`flex-1 py-3 rounded-lg font-bold text-sm border transition-all ${
                      targetGroup === 'WOMAN'
                        ? 'border-pink-400 bg-pink-400/20 text-pink-300'
                        : 'border-white/20 bg-black/20 text-white/40 hover:border-white/30'
                    }`}
                    data-testid="select-woman"
                  >
                    WOMAN
                  </button>
                </div>
              </div>
            )}

            {/* Purchaser Name */}
            <div>
              <label className="block text-white/60 text-sm mb-1">Your Name (optional)</label>
              <input
                type="text" value={purchaserName} onChange={e => setPurchaserName(e.target.value)}
                placeholder="Display name" maxLength={100}
                className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none"
                data-testid="input-purchaser-name"
              />
            </div>

            {/* Anonymous Toggle */}
            <div className="flex items-center justify-between py-1">
              <span className="text-white/60 text-sm">Stay Anonymous</span>
              <button
                type="button"
                onClick={() => setIsAnonymous(!isAnonymous)}
                className="flex items-center gap-1.5 text-sm"
                data-testid="toggle-anonymous"
              >
                {isAnonymous ? (
                  <ToggleRight size={28} style={{ color: config.color }} />
                ) : (
                  <ToggleLeft size={28} className="text-white/30" />
                )}
                <span className={isAnonymous ? 'font-medium' : 'text-white/30'} style={isAnonymous ? { color: config.color } : {}}>
                  {isAnonymous ? 'Yes' : 'No'}
                </span>
              </button>
            </div>

            {/* Message */}
            <div>
              <label className="block text-white/60 text-sm mb-1">Message (optional)</label>
              <textarea
                value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Any message to go with this..." rows={2} maxLength={500}
                className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none resize-none"
                data-testid="input-message"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 shrink-0" /> <span>{error}</span>
              </div>
            )}

            <button
              onClick={handlePayAndSubmit}
              disabled={!isFormValid()}
              className="w-full text-white font-semibold rounded-lg px-6 py-4 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: `linear-gradient(to right, ${config.color}, ${config.color}99)` }}
              data-testid="pay-and-submit-btn"
            >
              Pay {config.price} & Submit
            </button>
            <p className="text-white/30 text-xs text-center">Secure payment via Square. Action goes live after payment.</p>
          </motion.div>
        )}

        {step === 'processing' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-8 text-center" data-testid="room-action-processing">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: config.color }} />
            <p className="text-white font-medium">Preparing your action...</p>
            <p className="text-white/40 text-sm mt-1">Redirecting to payment</p>
          </motion.div>
        )}

        {step === 'confirming' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-8 text-center" data-testid="room-action-confirming">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: config.color }} />
            <p className="text-white font-medium">Verifying payment...</p>
            <p className="text-white/40 text-sm mt-1">Your action will go live automatically</p>
          </motion.div>
        )}

        {step === 'submitted' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-lg rounded-xl border border-green-400/30 p-8 text-center"
            data-testid="room-action-submitted"
          >
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Action Submitted!</h2>
            <p className="text-white/60 text-sm">
              <span className="font-medium" style={{ color: config.color }}>{config.title}</span> is now live on the Room Actions ticker.
            </p>
            <p className="text-white/40 text-xs mt-3">Payment verified successfully.</p>
            <a href="/" className="inline-block mt-6 text-white/60 hover:text-white text-sm font-medium" data-testid="back-home-link"
              style={{ color: `${config.color}AA` }}>
              &#8592; Back to Mediterranean Club
            </a>
          </motion.div>
        )}

        {step === 'error' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-red-500/10 backdrop-blur-lg rounded-xl border border-red-400/30 p-8 text-center" data-testid="room-action-error">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-white/60 text-sm">{error}</p>
            <button onClick={() => { setStep('form'); setError(''); }} className="mt-4 text-sm font-medium" style={{ color: config.color }}>
              Try again
            </button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default RoomActionPage;
export { ROOM_ACTION_CONFIG };
